/**
 * AI 공급자 카탈로그 — 운영자가 Claude/GPT/Gemini/Grok 중 골라 쓴다(docs/build/16 §4-5).
 * 순수 데이터·판정만. SDK 호출은 apps/edge/src/aiProviders.ts.
 *
 * 왜 카탈로그를 shared 에 두나: 관리자(드롭다운 라벨)·엣지(키·모델 해석)가 **같은 목록**을 봐야 한다.
 * 한쪽에만 공급자를 추가하면 관리자에 보이는데 서버가 모르는 유령 선택지가 생긴다.
 *
 * ⚠️ 모델 ID 는 공급자가 수시로 바꾼다(2026-07 기준 확인값). 틀리면 런타임 404 다.
 *    운영에서는 AI_MODEL_<PROVIDER> 환경변수로 덮어쓸 수 있게 해뒀다 — 코드 배포 없이 교체.
 */

export const AI_PROVIDERS = ["anthropic", "openai", "google", "xai"] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export interface AiProviderInfo {
  /** 관리자 드롭다운에 보일 이름(운영자는 "anthropic"이 아니라 "Claude"로 안다) */
  label: string;
  /** 2026-07 확인 기준 기본 모델. AI_MODEL_<PROVIDER> 로 덮어쓸 수 있다. */
  defaultModel: string;
  /** 엣지 시크릿 이름 */
  keyEnv: string;
  /** 모델 덮어쓰기 환경변수 이름 */
  modelEnv: string;
  /**
   * SDK 선택. openai-호환 계열(GPT·Grok·Gemini)은 `openai` SDK + baseUrl 로 통일한다 —
   * 공급자별 SDK 를 3개 더 넣으면 워커 번들만 커지고 추측할 API 표면도 늘어난다.
   */
  sdk: "anthropic" | "openai";
  /** openai SDK 사용 시 붙일 baseUrl(공식 OpenAI 는 기본값이라 없음) */
  baseUrl?: string;
  /** 운영자에게 보여줄 주의사항(있으면 관리자에 표시) */
  note?: string;
}

export const AI_PROVIDER_INFO: Record<AiProvider, AiProviderInfo> = {
  anthropic: {
    label: "Claude",
    defaultModel: "claude-opus-4-8",
    keyEnv: "ANTHROPIC_API_KEY",
    modelEnv: "AI_MODEL_ANTHROPIC",
    sdk: "anthropic",
  },
  openai: {
    label: "GPT",
    defaultModel: "gpt-5.6-sol", // 별칭 gpt-5.6 이 가리키는 모델. 카탈로그 id 와 일치시켜 드롭다운 선택이 잡히게.
    keyEnv: "OPENAI_API_KEY",
    modelEnv: "AI_MODEL_OPENAI",
    sdk: "openai",
  },
  google: {
    label: "Gemini",
    defaultModel: "gemini-3.5-flash", // gemini-flash-latest 가 가리키는 GA 모델
    keyEnv: "GOOGLE_API_KEY",
    modelEnv: "AI_MODEL_GOOGLE",
    sdk: "openai",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai/",
    note: "OpenAI 호환 계층을 통해 호출해요. 구조화 출력이 간혹 다르게 동작할 수 있어요.",
  },
  xai: {
    label: "Grok",
    defaultModel: "grok-4.5",
    keyEnv: "XAI_API_KEY",
    modelEnv: "AI_MODEL_XAI",
    sdk: "openai",
    baseUrl: "https://api.x.ai/v1",
    note: "OpenAI 호환 API 예요. 구조화 출력 지원은 공식 문서에서 확인되지 않았어요.",
  },
};

export function isAiProvider(v: unknown): v is AiProvider {
  return typeof v === "string" && (AI_PROVIDERS as readonly string[]).includes(v);
}

/** 관리자 드롭다운용 최소 정보(라벨·주의). 키 존재 여부는 엣지가 판단해 함께 내려준다. */
export interface AiProviderOption {
  id: AiProvider;
  label: string;
  note?: string;
}
export function aiProviderOptions(available: readonly AiProvider[]): AiProviderOption[] {
  return available.filter(isAiProvider).map((id) => ({
    id,
    label: AI_PROVIDER_INFO[id].label,
    ...(AI_PROVIDER_INFO[id].note ? { note: AI_PROVIDER_INFO[id].note } : {}),
  }));
}

/**
 * 쓸 공급자 결정: 요청값 → 기본값(사용 가능한 것 중 첫째).
 * 요청값이 사용 불가면 **조용히 다른 걸 쓰지 않는다** — 운영자가 Claude 를 골랐는데 Grok 이 쓰이면
 * 결과가 왜 다른지 알 수 없다. null 을 돌려 호출부가 명확히 거절하게 한다.
 */
export function pickAiProvider(
  requested: unknown,
  available: readonly AiProvider[],
): AiProvider | null {
  if (requested === undefined || requested === null || requested === "") {
    return available[0] ?? null;
  }
  if (!isAiProvider(requested)) return null;
  return available.includes(requested) ? requested : null;
}

// ── 모델 카탈로그·가격 ─────────────────────────────────────────
// ⚠️ 가격·모델은 공급자가 수시로 바꾼다. 아래는 AI_PRICE_ASOF 시점의 공개 가격이고,
//    **실제 청구는 공급자 계정 기준**이다. 관리자 UI 는 이 사실과 확인처 링크를 함께 보여준다.
//    모델이 사라지면 초안이 404 로 실패한다 → 운영자가 UI 에서 다른 모델을 고르면 된다.

export const AI_PRICE_ASOF = "2026-07";
/** 굿즈 초안 1건의 대략적 토큰(관찰 이미지 + 7콜). 비용 감각을 주기 위한 추정치. */
export const GOODS_DRAFT_TOKENS = { input: 10_600, output: 1_600 } as const;
/** 원화 환산 표시용(정확한 청구가 아니라 감각용). 환율은 늘 변한다. */
export const USD_KRW_APPROX = 1400;

export interface AiModelInfo {
  id: string;
  label: string;
  inputPer1M: number; // USD / 100만 입력 토큰
  outputPer1M: number; // USD / 100만 출력 토큰
  contextK?: number; // 컨텍스트 창(K 토큰)
  note?: string;
}

export const AI_MODEL_CATALOG: Record<AiProvider, AiModelInfo[]> = {
  anthropic: [
    { id: "claude-opus-4-8", label: "Claude Opus 4.8", inputPer1M: 5, outputPer1M: 25, contextK: 1000, note: "품질 우선(기본값)" },
    { id: "claude-sonnet-5", label: "Claude Sonnet 5", inputPer1M: 3, outputPer1M: 15, contextK: 1000, note: "균형" },
    { id: "claude-haiku-4-5", label: "Claude Haiku 4.5", inputPer1M: 1, outputPer1M: 5, contextK: 200, note: "가장 저렴·빠름" },
    { id: "claude-fable-5", label: "Claude Fable 5", inputPer1M: 10, outputPer1M: 50, contextK: 1000, note: "최고 성능·최고가" },
  ],
  openai: [
    { id: "gpt-5.6-sol", label: "GPT-5.6 Sol", inputPer1M: 5, outputPer1M: 30, note: "플래그십(기본값 gpt-5.6 이 가리킴)" },
    { id: "gpt-5.6-terra", label: "GPT-5.6 Terra", inputPer1M: 2.5, outputPer1M: 15, note: "균형" },
    { id: "gpt-5.6-luna", label: "GPT-5.6 Luna", inputPer1M: 1, outputPer1M: 6, note: "가장 저렴·빠름" },
  ],
  google: [
    { id: "gemini-3.5-flash", label: "Gemini 3.5 Flash", inputPer1M: 1.5, outputPer1M: 9, note: "최신 GA(기본값)" },
    { id: "gemini-3.1-pro", label: "Gemini 3.1 Pro", inputPer1M: 1, outputPer1M: 6, note: "3.5 Flash 보다 저렴" },
  ],
  xai: [
    { id: "grok-4.5", label: "Grok 4.5", inputPer1M: 2, outputPer1M: 6, note: "플래그십(기본값)" },
  ],
};

/** 공급자 가격표(운영자가 직접 확인할 곳 — 우리 표는 언제든 낡을 수 있다). */
export const AI_PRICING_URL: Record<AiProvider, string> = {
  anthropic: "https://platform.claude.com/docs/en/pricing",
  openai: "https://developers.openai.com/api/docs/pricing",
  google: "https://ai.google.dev/gemini-api/docs/pricing",
  xai: "https://docs.x.ai/developers/models",
};

export function findModel(provider: AiProvider, id: string): AiModelInfo | undefined {
  return AI_MODEL_CATALOG[provider].find((m) => m.id === id);
}

/** 굿즈 초안 1건의 예상 비용(USD). 목록가보다 이게 운영자에게 실질적이다. */
export function estimateDraftCostUsd(m: AiModelInfo): number {
  return (GOODS_DRAFT_TOKENS.input / 1e6) * m.inputPer1M + (GOODS_DRAFT_TOKENS.output / 1e6) * m.outputPer1M;
}

/** "약 ₩130" — 감각용 표시. 반올림해서 과한 정밀도로 오해를 주지 않는다. */
export function formatDraftCostKrw(m: AiModelInfo): string {
  const krw = estimateDraftCostUsd(m) * USD_KRW_APPROX;
  return `약 ₩${Math.round(krw / 10) * 10}`;
}

/**
 * 비밀 vs 설정 구분 — **모델 id 는 비밀이 아니다**(`gpt-5.6-luna` 는 숨길 게 없다).
 * 이걸 구분하지 않으면 모델도 끝 4자만 내려가 UI 가 "지금 무슨 모델을 쓰는지" 못 보여준다.
 * 모르는 이름은 **비밀로 친다**(fail-safe) — 새 비밀이 추가됐을 때 실수로 값이 새지 않게.
 */
export const AI_SECRET_ENVS: readonly string[] = AI_PROVIDERS.map((p) => AI_PROVIDER_INFO[p].keyEnv);
export const AI_SETTING_ENVS: readonly string[] = AI_PROVIDERS.map((p) => AI_PROVIDER_INFO[p].modelEnv);

export function isSecretEnvName(name: string): boolean {
  return !AI_SETTING_ENVS.includes(name);
}

/**
 * 공급자 모델 목록에서 **글 쓰는 모델만** 추린다.
 * OpenAI 계열은 임베딩·음성·이미지 모델까지 수십 개를 돌려줘 그대로 보여주면 운영자가 고를 수 없다.
 * 포함(화이트리스트)이 아니라 제외(블랙리스트)인 이유: 새 채팅 모델이 나오면 자동으로 보여야 하고,
 * 비채팅 계열 이름은 상대적으로 안정적이다.
 */
const NON_CHAT = /embed|whisper|tts|audio|speech|image|dall-e|moderation|rerank|vision-encoder|realtime|search-|transcribe/i;
export function pickChatModels(ids: readonly string[]): string[] {
  return [...new Set(ids)].filter((id) => !NON_CHAT.test(id)).sort();
}

/** 카탈로그(가격 있음) + 라이브 목록(가격 미상)을 합친 표시용 항목. */
export interface ModelRow {
  id: string;
  label: string;
  known: boolean; // 카탈로그에 있어 가격을 아는가
  live?: boolean; // 이번 조회에서 계정에 실제로 있었는가(undefined = 조회 안 함)
  info?: AiModelInfo;
}

/**
 * 표시용 병합. 라이브 목록을 조회했다면:
 * - 카탈로그에 있는데 라이브에 없다 → `live:false` (**404 의 원인** — 계정에서 사라진 모델)
 * - 라이브에만 있다 → 새 모델. 가격은 알 수 없다(API 가 가격을 주지 않는다).
 */
export function mergeModelRows(provider: AiProvider, liveIds?: readonly string[]): ModelRow[] {
  const catalog = AI_MODEL_CATALOG[provider];
  const live = liveIds ? new Set(pickChatModels(liveIds)) : null;

  const rows: ModelRow[] = catalog.map((info) => ({
    id: info.id,
    label: info.label,
    known: true,
    info,
    ...(live ? { live: live.has(info.id) } : {}),
  }));
  if (!live) return rows;

  for (const id of [...live].filter((id) => !catalog.some((m) => m.id === id))) {
    rows.push({ id, label: id, known: false, live: true });
  }
  return rows;
}
